import { Button, Col, Row, Space, Tabs } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProject } from '@/features/project/_hooks/project';
import { shouldShowPaymentGraph } from '@/features/project/_utils/payment';
import { useId } from '@/hooks';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { PreviewRowStateProvider } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import { ContractorPaymentsTable } from './ContractorPaymentsTable';
import { PaymentAmountBreakdownTable } from './PaymentAmountBreakdownTable';
import { PaymentEdgesTable } from './PaymentEdgesTable';
import { PaymentGraph } from './PaymentGraph';
import { PaymentGroupsTable } from './PaymentGroupsTable';

export const PaymentTabItems: FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { setDrawerState } = useProjectDetailDrawerState();
  const [activeKey, setActiveKey] = useState<'list' | 'payment-edge'>('list');

  return (
    <Tabs
      destroyInactiveTabPane
      activeKey={activeKey}
      onChange={(key) => {
        if (key === 'list' || key === 'payment-edge') {
          setActiveKey(key);
        }
      }}
      tabBarExtraContent={
        <ProjectResourceGuard onlyEdit>
          {project.data &&
            !project.data.isLocked &&
            project.data.sheetType !== 'KS' && (
              <PublishedProjectMutableGuard>
                <Row gutter={[8, 0]}>
                  <Col flex="auto" />
                  <Col flex="none">
                    <Button
                      onClick={() => {
                        setDrawerState({
                          open: true,
                          type: 'indirect-costs',
                        });
                      }}
                    >
                      {t('update-indirect-costs')}
                    </Button>
                  </Col>
                  {activeKey === 'payment-edge' && (
                    <Col flex="none">
                      <Button
                        onClick={() => {
                          setDrawerState({
                            open: true,
                            type: 'payment-edge',
                            isEditing: false,
                          });
                        }}
                      >
                        {t('create-payment-edge')}
                      </Button>
                    </Col>
                  )}
                </Row>
              </PublishedProjectMutableGuard>
            )}
        </ProjectResourceGuard>
      }
      type="card"
      size="small"
      items={[
        {
          key: 'list',
          label: t('list'),
          children: (
            <PreviewRowStateProvider>
              <Space direction="vertical" size={48} style={{ display: 'flex' }}>
                <PaymentGroupsTable />
                {project.data?.sheetType !== 'KS' && (
                  <>
                    <ContractorPaymentsTable />
                    <PaymentEdgesTable />
                  </>
                )}
                <PaymentAmountBreakdownTable />
              </Space>
            </PreviewRowStateProvider>
          ),
        },
        ...(project.data &&
        shouldShowPaymentGraph(project.data.fiscalYear, project.data.sheetType)
          ? [
              {
                key: 'payment-edge',
                label: t('payment-edge'),
                children: <PaymentGraph />,
              },
            ]
          : []),
      ]}
    />
  );
};
